/**
 * ParamtypeFormModal — the publish / reissue form for one global analysis-parameter-type. Two modes off
 * the same modal (mirrors the admin PricingPage modal idiom):
 *   - NEW   (`row === null`): the `code` is editable (the immutable taxonomy PK); POST publishes.
 *   - EDIT  (`row` set):      the `code` is locked (a correction is a REISSUE of the SAME code, never a
 *                             re-key — L-1); PUT reissues, the server bumps the publish version.
 *
 * The wire bodies are snake_case (matching the Zod schemas: `discipline_code`, `type_resultat`,
 * `methode_classe`). Client-side pre-validation mirrors the server (required fields + the code charset
 * regex) for instant UX; the server stays the authority and a typed CONFLICT/VALIDATION surfaces inline.
 * Accessible: FormField wires id/aria-describedby/aria-invalid; the error is role="alert".
 */
import { useState } from "react";
import { Button, FormField, Input, Modal, Select, useToast, type SelectOption } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import type {
  CreateParamtypeBody,
  ParamtypeDetail,
  ParamtypeTypeResultat,
  UpdateParamtypeBody,
} from "./paramtypes.types";
import { useCreateParamtype, useReissueParamtype } from "./paramtypes.api";

/** Mirror of the server `code` charset (paramtypes.validators.js): alnum + `._-`, no whitespace. */
const CODE_RE = /^[A-Za-z0-9._-]+$/;

const TYPE_VALUES: ReadonlyArray<ParamtypeTypeResultat> = ["numerique", "qualitatif", "texte"];

export interface ParamtypeFormModalProps {
  open: boolean;
  /** `null` = publish a new type; a row = reissue that code. */
  row: ParamtypeDetail | null;
  onClose: () => void;
}

export function ParamtypeFormModal({ open, row, onClose }: ParamtypeFormModalProps) {
  const { t } = useT();
  const toast = useToast();
  const create = useCreateParamtype();
  const reissue = useReissueParamtype();
  const isEdit = !!row;

  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [type, setType] = useState<ParamtypeTypeResultat>("numerique");
  const [unite, setUnite] = useState("");
  const [methode, setMethode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<"code" | "libelle" | "discipline" | null>(null);

  // Seed the form once per open-target (the render-time seed idiom from the admin PricingPage modal).
  const seedKey = row?.code ?? (open ? "_new" : "_closed");
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setCode(row?.code ?? "");
    setLibelle(row?.libelle ?? "");
    setDiscipline(row?.disciplineCode ?? "");
    setType(row?.typeResultat ?? "numerique");
    setUnite(row?.unite ?? "");
    setMethode(row?.methodeClasse ?? "");
    setError(null);
    setFieldErr(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  const typeOptions: ReadonlyArray<SelectOption> = TYPE_VALUES.map((v) => ({
    value: v,
    label: t(`mgmt.paramtypes.type.${v}`),
  }));

  const pending = create.isPending || reissue.isPending;

  const submit = () => {
    setError(null);
    setFieldErr(null);
    const codeV = code.trim();
    const libelleV = libelle.trim();
    const disciplineV = discipline.trim();

    if (!isEdit) {
      if (!codeV) {
        setFieldErr("code");
        setError(t("mgmt.paramtypes.field.required"));
        return;
      }
      if (!CODE_RE.test(codeV)) {
        setFieldErr("code");
        setError(t("mgmt.paramtypes.field.code.invalid"));
        return;
      }
    }
    if (!libelleV) {
      setFieldErr("libelle");
      setError(t("mgmt.paramtypes.field.required"));
      return;
    }
    if (!disciplineV) {
      setFieldErr("discipline");
      setError(t("mgmt.paramtypes.field.required"));
      return;
    }

    const onError = (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t("mgmt.paramtypes.toast.error"));

    if (isEdit) {
      // A reissue is a partial patch — send only the editable fields (the server bumps the version).
      const body: UpdateParamtypeBody = {
        libelle: libelleV,
        discipline_code: disciplineV,
        type_resultat: type,
        unite: unite.trim() ? unite.trim() : null,
        methode_classe: methode.trim() ? methode.trim() : null,
      };
      reissue.mutate(
        { code: row!.code, body },
        {
          onSuccess: () => {
            toast.success(t("mgmt.paramtypes.toast.reissued"));
            onClose();
          },
          onError,
        },
      );
      return;
    }

    const body: CreateParamtypeBody = {
      code: codeV,
      libelle: libelleV,
      discipline_code: disciplineV,
      type_resultat: type,
      ...(unite.trim() ? { unite: unite.trim() } : {}),
      ...(methode.trim() ? { methode_classe: methode.trim() } : {}),
    };
    create.mutate(body, {
      onSuccess: () => {
        toast.success(t("mgmt.paramtypes.toast.published"));
        onClose();
      },
      onError,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? t("mgmt.paramtypes.modal.title.edit", { code: row!.code })
          : t("mgmt.paramtypes.modal.title.new")
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.paramtypes.modal.cancel")}
          </Button>
          <Button onClick={submit} loading={pending}>
            {isEdit ? t("mgmt.paramtypes.modal.submit.edit") : t("mgmt.paramtypes.modal.submit.new")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField
          label={t("mgmt.paramtypes.modal.code")}
          hint={t("mgmt.paramtypes.modal.code.hint")}
          required
          {...(fieldErr === "code" ? { error: error } : {})}
        >
          {(p) => (
            <Input
              {...p}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isEdit}
              mono
              className="nv-ltr-island"
              autoComplete="off"
            />
          )}
        </FormField>

        <FormField
          label={t("mgmt.paramtypes.modal.libelle")}
          required
          {...(fieldErr === "libelle" ? { error: error } : {})}
        >
          {(p) => <Input {...p} value={libelle} onChange={(e) => setLibelle(e.target.value)} />}
        </FormField>

        <FormField
          label={t("mgmt.paramtypes.modal.discipline")}
          hint={t("mgmt.paramtypes.modal.discipline.hint")}
          required
          {...(fieldErr === "discipline" ? { error: error } : {})}
        >
          {(p) => (
            <Input
              {...p}
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
              mono
              className="nv-ltr-island"
              autoComplete="off"
            />
          )}
        </FormField>

        <FormField label={t("mgmt.paramtypes.modal.type")} required>
          {(p) => (
            <Select
              {...p}
              value={type}
              onChange={(e) => setType(e.target.value as ParamtypeTypeResultat)}
              options={typeOptions}
            />
          )}
        </FormField>

        <FormField label={t("mgmt.paramtypes.modal.unite")}>
          {(p) => (
            <Input
              {...p}
              value={unite}
              onChange={(e) => setUnite(e.target.value)}
              placeholder={t("mgmt.paramtypes.modal.unite.placeholder")}
              mono
              className="nv-ltr-island"
            />
          )}
        </FormField>

        <FormField label={t("mgmt.paramtypes.modal.methode")}>
          {(p) => (
            <Input
              {...p}
              value={methode}
              onChange={(e) => setMethode(e.target.value)}
              placeholder={t("mgmt.paramtypes.modal.methode.placeholder")}
            />
          )}
        </FormField>

        {isEdit && (
          <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.paramtypes.modal.edit.note")}
          </p>
        )}

        {/* A non-field-specific error (e.g. a server CONFLICT) renders below the form. */}
        {error && fieldErr === null && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
